import type { Session } from '@supabase/supabase-js'
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { supabase } from '../lib/supabase'
import type { UserProfile } from './permissions'
import { createRequestVersion } from './requestVersion'

interface AuthContextValue {
  error: string | null
  loading: boolean
  profile: UserProfile | null
  session: Session | null
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

async function loadProfile(userId: string): Promise<UserProfile> {
  let { data, error } = await supabase
    .from('profiles')
    .select('id, full_name, role, active, must_change_password')
    .eq('id', userId)
    .single<UserProfile>()

  // Keep the production login available between the frontend deploy and the
  // controlled hosted migration. The fallback disappears naturally as soon as
  // the column exists.
  if (error?.code === '42703' || error?.code === 'PGRST204') {
    const legacy = await supabase
      .from('profiles')
      .select('id, full_name, role, active')
      .eq('id', userId)
      .single<Omit<UserProfile, 'must_change_password'>>()

    data = legacy.data
      ? { ...legacy.data, must_change_password: false }
      : null
    error = legacy.error
  }

  if (error || !data) {
    throw new Error('Seu perfil de acesso ainda não foi configurado.')
  }

  if (data.id !== userId || !['ADMIN', 'GESTAO'].includes(data.role)) {
    throw new Error('Perfil de acesso inválido.')
  }

  if (data.active !== true) {
    throw new Error('Seu acesso está inativo. Procure um administrador.')
  }

  return data
}

export function AuthProvider({ children }: PropsWithChildren) {
  const requestVersion = useRef(createRequestVersion())
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    const requests = requestVersion.current
    let receivedEvent = false

    async function synchronize(nextSession: Session | null) {
      if (!active) return
      const isCurrent = requests.next()

      setLoading(true)
      if (nextSession) setError(null)
      setSession(nextSession)
      setProfile(null)

      if (!nextSession) {
        setProfile(null)
        setLoading(false)
        return
      }

      try {
        // Leave the auth notification stack before calling the Supabase client.
        await new Promise<void>((resolve) => setTimeout(resolve, 0))
        if (!active || !isCurrent()) return
        const nextProfile = await loadProfile(nextSession.user.id)
        if (active && isCurrent()) setProfile(nextProfile)
      } catch (cause) {
        if (active && isCurrent()) {
          setProfile(null)
          setSession(null)
          setError(
            cause instanceof Error
              ? cause.message
              : 'Não foi possível validar seu acesso.',
          )
        }
      } finally {
        if (active && isCurrent()) setLoading(false)
      }
    }

    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      receivedEvent = true
      void synchronize(nextSession)
    })

    void supabase.auth.getSession().then(({ data, error: sessionError }) => {
      if (!active || receivedEvent) return
      if (sessionError) throw sessionError
      return synchronize(data.session)
    }).catch(() => {
      if (!active || receivedEvent) return
      setSession(null)
      setProfile(null)
      setError('Não foi possível recuperar a sessão. Tente entrar novamente.')
      setLoading(false)
    })

    return () => {
      active = false
      requests.invalidate()
      data.subscription.unsubscribe()
    }
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      error,
      loading,
      profile,
      session,
      signOut: async () => {
        requestVersion.current.invalidate()
        setLoading(false)
        setProfile(null)
        setSession(null)
        try {
          const { error: signOutError } = await supabase.auth.signOut({ scope: 'local' })
          if (signOutError) throw signOutError
        } catch {
          setError('Não foi possível encerrar a sessão. Verifique a conexão e tente novamente.')
        }
      },
    }),
    [error, loading, profile, session],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth deve ser utilizado dentro de AuthProvider.')
  }

  return context
}
